// Replay list screen: shows recent matches with the ability to watch replays

import { MatchSummary } from './ReplayTypes';
import { WIZARD_CONFIGS, WizardType } from '../wizards/WizardTypes';

export class ReplayListUI {
  private container: HTMLElement;
  private screen: HTMLElement | null = null;
  private _onSelectMatch: ((matchId: string) => void) | null = null;
  private _onBack: (() => void) | null = null;

  constructor() {
    this.container = document.getElementById('ui-overlay')!;
  }

  onSelectMatch(cb: (matchId: string) => void): void {
    this._onSelectMatch = cb;
  }

  onBack(cb: () => void): void {
    this._onBack = cb;
  }

  show(matches: MatchSummary[]): void {
    this.hide();

    this.screen = document.createElement('div');
    this.screen.className = 'menu-screen';
    this.screen.style.cssText = `
      display: flex; flex-direction: column; align-items: center;
      padding: 40px 20px; box-sizing: border-box;
      overflow-y: auto; max-height: 100vh;
    `;

    const title = document.createElement('h2');
    title.className = 'game-title';
    title.style.fontSize = '2.2rem';
    title.textContent = 'Match Replays';
    this.screen.appendChild(title);

    const ornament = document.createElement('div');
    ornament.className = 'ornament';
    this.screen.appendChild(ornament);

    if (matches.length === 0) {
      const empty = document.createElement('p');
      empty.style.cssText = `color: #888; font-family: 'Cinzel', serif; font-size: 1rem; margin: 2rem 0;`;
      empty.textContent = 'No replay data available yet. Play some matches!';
      this.screen.appendChild(empty);
    } else {
      const list = document.createElement('div');
      list.style.cssText = `
        display: flex; flex-direction: column; gap: 8px;
        width: 100%; max-width: 600px; margin: 1rem 0;
      `;

      for (const match of matches) {
        const card = this.createMatchCard(match);
        list.appendChild(card);
      }

      this.screen.appendChild(list);
    }

    const backBtn = document.createElement('button');
    backBtn.className = 'menu-button';
    backBtn.style.cssText += 'font-size: 1rem; padding: 0.8rem 2rem; margin-top: 1rem;';
    backBtn.textContent = 'Back';
    backBtn.addEventListener('click', () => {
      if (this._onBack) this._onBack();
    });
    this.screen.appendChild(backBtn);

    this.container.appendChild(this.screen);
  }

  hide(): void {
    if (this.screen) {
      this.screen.remove();
      this.screen = null;
    }
  }

  private createMatchCard(match: MatchSummary): HTMLElement {
    const card = document.createElement('div');
    card.style.cssText = `
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px; cursor: pointer;
      background: rgba(42, 42, 42, 0.6);
      border: 1px solid rgba(201, 162, 39, 0.3);
      border-radius: 6px; transition: all 0.2s;
      font-family: 'Cinzel', serif;
    `;

    card.addEventListener('mouseenter', () => {
      card.style.background = 'rgba(201, 162, 39, 0.15)';
      card.style.borderColor = '#C9A227';
    });
    card.addEventListener('mouseleave', () => {
      card.style.background = 'rgba(42, 42, 42, 0.6)';
      card.style.borderColor = 'rgba(201, 162, 39, 0.3)';
    });
    card.addEventListener('click', () => {
      if (this._onSelectMatch) this._onSelectMatch(match.matchId);
    });

    const p1Config = WIZARD_CONFIGS[match.player1Wizard as WizardType];
    const p2Config = WIZARD_CONFIGS[match.player2Wizard as WizardType];
    const winnerLabel =
      match.winner === 'player1'
        ? match.player1Name
        : match.winner === 'player2'
          ? match.player2Name
          : 'Draw';

    const date = new Date(match.timestamp);
    const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const durationSec = Math.floor(match.duration / 1000);
    const durationStr = `${Math.floor(durationSec / 60)}:${(durationSec % 60).toString().padStart(2, '0')}`;

    card.innerHTML = `
      <span style="font-size: 1.3rem;">${p1Config?.icon || ''}</span>
      <div style="flex: 1; display: flex; flex-direction: column; gap: 2px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #E8DCC4; font-size: 0.9rem;">
            ${match.player1Name} <span style="color: #666;">vs</span> ${match.player2Name}
          </span>
          <span style="color: #888; font-size: 0.75rem;">${dateStr}</span>
        </div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #C9A227; font-size: 0.8rem;">Winner: ${winnerLabel}</span>
          <span style="color: #888; font-size: 0.75rem;">${durationStr}</span>
        </div>
      </div>
      <span style="font-size: 1.3rem;">${p2Config?.icon || ''}</span>
      <span style="color: #C9A227; font-size: 0.85rem; margin-left: 8px;">&#9654;</span>
    `;

    return card;
  }
}
